import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Dialog,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Stack,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTenantStore } from '../../../stores/tenant.store';
import type { CampaignDraft } from './types';
import { formatDateTime } from '../../../lib/i18n-format';
import { audienceLabel } from './audience-labels';
import { callable } from '../../../lib/callable';

interface CampaignRecord {
  id: string;
  subject: string;
  status: string;
  audienceType: string;
  audienceSegment: any;
  scheduledAt: string | null;
  sentAt: string | null;
  sentCount: number;
  failedCount: number;
  updatedAt: string | null;
  createdAt: string | null;
  blocksJson: string | null;
}

interface Props {
  onResume: (draft: CampaignDraft & { draftId: string }) => void;
}

const listCampaignsFn = callable('listCampaigns');
const deleteDraftFn = callable('deleteCampaignDraft');

export default function DraftsList({ onResume }: Props) {
  const { currentTenantId: tenantId } = useTenantStore();
  const [drafts, setDrafts] = useState<CampaignRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const { t } = useTranslation('sis');
  const [toDelete, setToDelete] = useState<CampaignRecord | null>(null);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await listCampaignsFn({ tenantId, statuses: ['draft'] });
      const data = res.data as { items: CampaignRecord[] };
      setDrafts(data.items);
    } catch (e: any) {
      setError(e.message ?? t('campaign.drafts.loadError'));
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (id: string) => {
    if (!tenantId) return;
    setDeleting(id);
    try {
      await deleteDraftFn({ tenantId, draftId: id });
      setDrafts((prev) => prev.filter((d) => d.id !== id));
    } catch (e: any) {
      setError(e.message ?? t('campaign.drafts.deleteError'));
    } finally {
      setDeleting(null);
    }
  };

  const handleResume = (draft: CampaignRecord) => {
    const blocks = draft.blocksJson ? JSON.parse(draft.blocksJson) : [];
    onResume({
      draftId: draft.id,
      subject: draft.subject,
      blocks,
      audienceSegment: draft.audienceSegment ?? { type: 'all_crm' },
    });
  };

  if (loading)
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {drafts.length === 0
            ? t('campaign.drafts.emptyList')
            : t('campaign.drafts.pending', { count: drafts.length })}
        </Typography>
        <Tooltip title={t('campaign.tabs.refreshList')}>
          <IconButton size="small" onClick={load}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {drafts.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
          <Typography variant="body1">{t('campaign.drafts.empty')}</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>{t('campaign.tabs.subject')}</strong>
                </TableCell>
                <TableCell>
                  <strong>{t('campaign.tabs.audience')}</strong>
                </TableCell>
                <TableCell>
                  <strong>{t('campaign.drafts.lastEdited')}</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>{t('campaign.tabs.actions')}</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {drafts.map((draft) => (
                <TableRow key={draft.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {/* UN BORRADOR SIN ASUNTO ES NORMAL, no un error: el
                          asunto se exige al enviar. El texto es de pantalla y
                          el dato queda vacío, así que el día que se escriba no
                          hay que borrar ningún relleno. */}
                      {draft.subject || (
                        <Box component="em" sx={{ color: 'text.disabled' }}>
                          {t('campaign.draft.noSubject')}
                        </Box>
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={audienceLabel(draft.audienceType)}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {draft.updatedAt ? formatDateTime(draft.updatedAt) : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title={t('campaign.drafts.resume')}>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<EditIcon />}
                          onClick={() => handleResume(draft)}
                          sx={{ textTransform: 'none' }}
                        >
                          {t('campaign.drafts.resume')}
                        </Button>
                      </Tooltip>
                      <Tooltip title={t('campaign.drafts.delete')}>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={deleting === draft.id}
                          onClick={() => setToDelete(draft)}
                        >
                          {deleting === draft.id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={toDelete !== null} onClose={() => setToDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('campaign.editor.deleteDraftTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('campaign.editor.deleteDraftBody', { subject: toDelete?.subject ?? '' })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToDelete(null)}>{t('campaign.editor.cancel')}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              const id = toDelete?.id;
              setToDelete(null);
              if (id) void handleDelete(id);
            }}
          >
            {t('campaign.editor.deleteConfirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
